import React, { useEffect, useState, useRef } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import {
  Row, Col, Spinner, Input, Label,
  FormGroup, ListGroup, ListGroupItem,
  FormFeedback,
} from 'reactstrap';

import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { uploadToS3 } from '@services/UploadService';
import Alert from '@shared/components/alert/Alert';
import InputField from '@shared/components/input/InputField';
import useTranslation from '@shared/hooks/useTranslation';
import {
  CameraIcon,
} from '@shared/icons';
import FileUploader, { FileUploaderRef } from '@shared/utils/FileUploader/FileUploader';

import '../RegisterPageContentStyle.scss';

type LaboratoryInputFieldsProps = {
  formData: Record<string, any>;
  setFormData: (e: any) => void;
  errors?: Record<string, string>;
  prefilledEmail?: string;
};

const LaboratoryInputFields = ({
  formData, setFormData, errors, prefilledEmail,
}: LaboratoryInputFieldsProps) => {
  const i18nRegistration = useTranslation('registration');
  const i18nLaboratory = useTranslation('registration.types.laboratory');
  const profilePhotoUploader = useRef<FileUploaderRef>(null);
  const [ isFocused, setIsFocused ] = useState(false);
  const [ uploading, setUploading ] = useState(false);
  const {
    placePredictions,
    placesService,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
  });

  const handleFileAdded = async (res: File[]) => {
    setUploading(true);
    try {
      const file = res[0];
      const link = await uploadToS3('profile', file, {
        name: file.name,
        type: file.type,
      });
      setFormData((s: any) => ({
        ...s,
        image: link,
        profile_picture_preview: URL.createObjectURL(res[0]),
      }));
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleOnFocus = () => setIsFocused(true);
  const handleOnBlur = () => {
    // Delay closing the list group to allow clicking on an item
    setTimeout(() => setIsFocused(false), 200);
  };

  const onSelectAddress = async (data: any) => {
    try {
      await placesService.getDetails({ placeId: data.place_id }, (placeDetails: any) => {
        if (!placeDetails) return;
        const { geometry, address_components: addressComponents } = placeDetails;
        const newFormData: any = {
          street: '',
          city: '',
          province: '',
          postal_code: '',
          gps_location: '',
        };
        addressComponents.forEach((component: any) => {
          const { types, long_name: longName } = component;

          if (types.includes('street_number')) {
            newFormData.street = `${longName} `;
          }
          if (types.includes('route')) {
            newFormData.street += longName;
          }
          if (types.includes('locality')) {
            newFormData.city = longName;
          }
          if (types.includes('administrative_area_level_1')) {
            newFormData.province = longName;
          }
          if (types.includes('postal_code')) {
            newFormData.postal_code = longName;
          }
        });

        if (geometry && geometry.location) {
          // Extract latitude and longitude
          const { lat, lng } = geometry.location;
          newFormData.gps_location = `${lat()}, ${lng()}`;
        }

        setFormData((prevState: any) => ({
          ...prevState,
          ...newFormData,
        }));
      });
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    setFormData({
      name: '',
      email: prefilledEmail || '',
      mobile_number: '',
      phone_number: '',
      street: '',
      city: '',
      province: '',
      postal_code: '',
      password: '',
      password_confirmation: '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Row>
        <Col>
          <div className="s1 title">
            {i18nLaboratory.description}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="s5 basic-info-style">
            {i18nRegistration.basicInfo}
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="text-center">
          <div className="photo-uploader mx-auto" onClick={() => profilePhotoUploader.current?.open()}>
            <FileUploader
              ref={profilePhotoUploader}
              onDrop={(ac) => handleFileAdded(ac)}
              accept={{
                'image/*': [ '.jpeg', '.jpg', '.png' ],
              }}
              maxSize={1e8}
              disabled={uploading}
            />
            {formData.profile_picture_preview && (
              <img src={formData.profile_picture_preview} alt="Profile" />
            )}
            {!formData.profile_picture_preview && !uploading && (
              <CameraIcon />
            )}
            {uploading && (
              <Spinner color="primary" size="md" className="spinner" />
            )}
          </div>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col className="text-center">
          {LocaleService.parseTranslationElement(i18nLaboratory.logo, {
            // eslint-disable-next-line react/no-unstable-nested-components
            optional: (optional: string) => (
              <span className="logo-optional-style">{optional}</span>
            ),
          })}
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-lab-name"
            name="name"
            label={i18nLaboratory.input.labName.label}
            placeholder={i18nLaboratory.input.labName.placeholder}
            type="text"
            required
            value={formData?.name}
            fieldError={errors?.name ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              name: value,
            }))}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-lab-email"
            name="email"
            label={i18nLaboratory.input.labEmail.label}
            placeholder={i18nLaboratory.input.labEmail.placeholder}
            type="email"
            required
            value={formData?.email}
            fieldError={errors?.email ?? ''}
            isReadOnly={!!prefilledEmail}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              email: value,
            }))}
          />
          {!!prefilledEmail && (
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                marginTop: '-12px',
                marginBottom: '10px',
              }}
            >
              This is the email your invitation was sent to. Your referral
              will only appear if you register with this email.
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-lab-phone"
            name="phone_number"
            label={i18nLaboratory.input.phone.label}
            placeholder={i18nLaboratory.input.phone.placeholder}
            type="text"
            required
            value={formData?.phone_number}
            fieldError={errors?.phone_number ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              phone_number: value,
            }))}
            isMobile
            mobileCountryCode="+1"
            customClassName="custom-mobile-number-form-padding"
            formGrpClassName={!errors?.phone_number ? 'mobile-input-grp' : ''}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-lab-mobile"
            name="mobile_number"
            label={i18nLaboratory.input.mobile.label}
            placeholder={i18nLaboratory.input.mobile.placeholder}
            type="text"
            required
            value={formData?.mobile_number}
            fieldError={errors?.mobile_number ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              mobile_number: value,
            }))}
            isMobile
            mobileCountryCode="+1"
            customClassName="custom-mobile-number-form-padding"
            formGrpClassName={!errors?.mobile_number ? 'mobile-input-grp' : ''}
          />
          <div className="mobile-description">
            {!errors?.mobile_number && (
              <span>{i18nLaboratory.input.mobile.description}</span>
            )}
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="s5 basic-info-style">
            {i18nLaboratory.otherInfo}
          </div>
        </Col>
      </Row>
      {
        errors?.gps_location && errors.gps_location.length > 0 && (
          <Row>
            <Col>
              <div className="d-grid gap-2">
                <Alert color="error">{i18nRegistration.error.gpsLocationError}</Alert>
              </div>
            </Col>
          </Row>
        )
      }
      <Row>
        <Col>
          <FormGroup floating className="form-group autocomplete-address-grp">
            <Input
              id="input-street"
              name="street"
              placeholder={i18nLaboratory.input.street.placeholder}
              type="text"
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              defaultValue={formData.street}
              value={formData.street}
              fieldError={errors?.street ?? ''}
              onChange={(evt) => {
                setFormData((prevState: any) => ({
                  ...prevState,
                  street: evt.target.value,
                }));
                getPlacePredictions({ input: evt.target.value });
              }}
            />
            {placePredictions.length > 0 && isFocused && (
              <ListGroup className="address-list-group">
                {placePredictions.map(prediction => (
                  <ListGroupItem
                    key={prediction.place_id}
                    onClick={() => {
                      onSelectAddress(prediction);
                    }}
                    style={{ cursor: 'pointer' }}
                    action
                  >
                    {prediction.description}
                  </ListGroupItem>
                ))}
              </ListGroup>
            )}
            <Label>
              <span className="required-symbol">* </span>{i18nLaboratory.input.street.label}
            </Label>
            <FormFeedback>{errors?.street ?? ''}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-city"
            name="city"
            label={i18nLaboratory.input.city.label}
            placeholder={i18nLaboratory.input.city.placeholder}
            type="text"
            required
            defaultValue={formData?.city}
            value={formData?.city}
            fieldError={errors?.city ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              city: value,
            }))}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <InputField
            id="input-province"
            name="province"
            label={i18nLaboratory.input.province.label}
            placeholder={i18nLaboratory.input.province.placeholder}
            type="text"
            required
            defaultValue={formData?.province}
            value={formData?.province}
            fieldError={errors?.province ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              province: value,
            }))}
          />
        </Col>
        <Col md={6}>
          <InputField
            id="input-postal"
            name="postal_code"
            label={i18nLaboratory.input.postal.label}
            placeholder={i18nLaboratory.input.postal.placeholder}
            type="text"
            required
            defaultValue={formData?.postal_code}
            value={formData?.postal_code}
            fieldError={errors?.postal_code ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              postal_code: value,
            }))}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="s5 basic-info-style">
            {i18nRegistration.securityInfo}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-password"
            name="password"
            label={i18nLaboratory.input.password.label}
            placeholder={i18nLaboratory.input.password.placeholder}
            type="password"
            required
            value={formData?.password}
            fieldError={errors?.password ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              password: value,
            }))}
            isPasswordStrength
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-password_confirmation"
            name="password_confirmation"
            label={i18nLaboratory.input.passwordConfirmation.label}
            placeholder={i18nLaboratory.input.passwordConfirmation.placeholder}
            type="password"
            required
            value={formData?.password_confirmation}
            fieldError={errors?.password_confirmation ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              password_confirmation: value,
            }))}
          />
        </Col>
      </Row>
    </>
  );
};

export default LaboratoryInputFields;
